import { BaseEntity, RestMethod } from '@plusng/react-common-components';
import { useRestOperations } from '../src/common/rest/restOperations';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pageable } from '../src/api';
import { useRetryRequestContext } from '../src/components/contexts/RetryRequestContext';
import { RequestResolutionOptions } from '../src/utils/interfaces';

const STALETIME_IN_MILLISECONDS = 600000;

export type QUERY_KEY =
    | 'ADDITIVE'
    | 'BASEOIL'
    | 'COSTS'
    | 'COMPANIES'
    | 'CONTAINERSIZE'
    | 'SHEETS'
    | 'TESTAREAS'
    | 'TESTGROUPS'
    | 'TESTSTEPS'
    | 'TESTPARAMETERS'
    | 'MANAGETESTPARAMETERS'
    | 'MBSPEC'
    | 'OP_FLUIDS'
    | 'PRODUCTS'
    | 'PROCESSES'
    | 'PRODUCT-TYPE'
    | 'QUALITYLINES'
    | 'ENGINE_TYPES'
    | 'TEST_STAND'
    | 'TEST_SUBSTANCES'
    | 'INVOICE_DATA'
    | 'TEST_BENCH_RESULTS'
    | 'FLUID-FINDER'
    | 'SALESAREAS'
    | 'SAECLASSES'
    | 'INVOICES'
    | 'PRODUCT-SHEETS'
    | 'PRODUCT-FORMULATIONS'
    | 'VEHICLES'
    | 'ALLTESTSTRUCTURES'
    | 'ADMIN-PRODUCTS-OVERVIEW'
    | 'ENGINE-OIL-LETTER-NUMBERS'
    | 'ADMIN-PRODUCTS-PROCESSES'
    | 'TEST-STEP-VALUES'
    | 'PRODUCT-RELEASES'
    | 'TEST-STEP-VALUES-FULFILLED'
    | 'PRODUCT-APPROVAL-CHECK-STEPS'
    | 'PRODUCT-APPROVALS'
    | 'PRODUCT-INVOICES'
    | 'ENGINE-OIL-TEST'
    | 'ENGINE-TYPES'
    | 'FUELS'
    | 'INVOICE-AMOUNT'
    | 'THIRD-PARTY-CONTRACTS'
    | 'COMPANY-REGISTRATION'
    | 'COMPANY-REGISTRATION-TO-APPROVE'
    | 'USER-INFO'
    | 'FAQS'
    | 'COMPANY-REGISTRATION-COUNTER'
    | 'LEGACY-COMPANIES'
    | 'FILES'
    | 'SERVICE-AREAS'
    | 'DBL-SPECIFICATIONS'
    | 'DBL-CATEGORIES'
    | 'INVOICES_USER'
    | 'COMPANY-REGISTRATION-TO-CHANGE'
    | 'PRODUCT-FORMULATIONS_ONLY_FULFILLED'
    | 'ANNUAL-REPORTS'
    | 'TASKS'
    | 'ADDITIVE-BASEOIL-TYPES';

interface CachedPaginatedDataProps {
    url: string;
    pagination?: Pageable;
    paginationDisableSorting?: boolean;
    queryKey: QUERY_KEY;
    dataId?: string;
    refetch?: boolean;
    staleTimeInSeconds?: number;
    enabled?: boolean;
    useCache?: boolean;
    keepPrevious?: boolean;
    searchText?: string;
    queryParams?: Record<string, string | undefined>;
}

interface CreateCachedItemProps<T> {
    /**
     * Static request URL used for the mutation.
     *
     * Use this when the endpoint does not depend on the payload.
     * If `getUrl` is provided, it takes precedence over this value.
     */
    url?: string;
    method: RestMethod;
    queryKey: QUERY_KEY;
    /**
     * Builds the request URL dynamically based on the mutation payload.
     *
     * Use this when the endpoint depends on values inside `data` (e.g., an id).
     * If provided, `getUrl(data)` takes precedence over `url`.
     */
    getUrl?: (data: T) => string;
    /**
     * Performs a mutation with conflict resolution support.
     *
     * @param conflictResolutionOptions - Options that control how conflicts (409) and not-found errors (404) are handled:
     *
     */
    conflictResolutionOptions?: RequestResolutionOptions;
}
/**
 * Fetches paginated data from the API.
 *
 * @param props - CachedPaginatedData configuration
 * @param props.pagination - Pagination settings (data will refresh when pagination changes)
 * @param props.paginationDisableSorting - Whether to disable sorting in pagination
 * @param props.useCache - Whether to enable caching of data (default: 10 minutes) Default: no cache. useCache=true enables cache
 * @param props.searchText - Custom search text for filtering results (data will refresh when searchText changes)
 *
 * @returns Promise with the paginated data
 */

// Custom hook to fetch paginated data
function useGetCachedPaginatedData<T extends BaseEntity | {} | null>({
    url,
    pagination,
    paginationDisableSorting,
    queryKey,
    dataId,
    refetch = false,
    staleTimeInSeconds, // Seconds (converted to ms)
    enabled,
    useCache = false, //useCache = false → Cache & staleTime have no effect (always load fresh)
    keepPrevious = false,
    searchText,
    queryParams,
}: CachedPaginatedDataProps) {
    const { doGetRequest } = useRestOperations<T>();

    const getUrl = (url: string) => {
        const params: string[] = [];

        if (searchText) {
            params.push(`search=${encodeURIComponent(searchText)}`);
        }

        if (queryParams) {
            Object.entries(queryParams).forEach(([key, value]) => {
                if (value) {
                    params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
                }
            });
        }

        if (pagination) {
            params.push(`page=${pagination.page}`);
            params.push(`size=${pagination.size}`);
            if (!paginationDisableSorting && pagination.sort?.length) {
                params.push(`sort=${pagination.sort.join(',')}`);
            }
        }

        if (params.length > 0) {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}${params.join('&')}`;
        }
        return url;
    };

    const getPaginatedData = async (url: string) => {
        return await doGetRequest({
            queryString: getUrl(url),
        });
    };
    let staleTime = 0;

    if (useCache) {
        const staleTimeInMilliseconds = staleTimeInSeconds ? staleTimeInSeconds * 1000 : undefined;
        staleTime = staleTimeInMilliseconds ?? STALETIME_IN_MILLISECONDS;
    }

    const query = useQuery({
        queryKey: [
            queryKey,
            url,  // URL as part of cache key - important for search!
            dataId,
            pagination ? pagination.page : '',
            pagination ? pagination.size : '',
            pagination ? pagination.sort : '',
            searchText,
            queryParams,
        ],
        queryFn: () => getPaginatedData(url),
        staleTime: staleTime,
        gcTime: useCache ? undefined : 0,
        refetchInterval: refetch && STALETIME_IN_MILLISECONDS,
        enabled: enabled ?? true,
        refetchOnWindowFocus: false,
        placeholderData: keepPrevious ? keepPreviousData : undefined,
    });

    return query;
}

function useUpdateCachedItem<T extends BaseEntity | {} | null>({
    url,
    method,
    queryKey,
    getUrl,
    conflictResolutionOptions,
}: CreateCachedItemProps<T>) {
    const { doUpdateDataRequest } = useRestOperations<T | null>();
    const queryClient = useQueryClient();
    const { setLastQueryKey } = useRetryRequestContext();

    const createDataRequest = (data: T | null) => {
        const resolvedUrl = getUrl && data ? getUrl(data) : (url ?? '');

        setLastQueryKey(queryKey);
        return doUpdateDataRequest({
            method: method,
            data: data,
            queryString: resolvedUrl,
            onConflict: {
                ...conflictResolutionOptions?.onConflict,
            },

            navigateTo: conflictResolutionOptions?.navigateTo,
        });
    };
    return useMutation({
        mutationFn: (data: T | null) => createDataRequest(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
        },
    });
}

export { useGetCachedPaginatedData, useUpdateCachedItem };
